import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Modal,
  TextField,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import { useParams, useNavigate, useLocation, Navigate } from 'react-router';
import TerminalLayout from '../layout/TerminalLayout';
import TerminalOrangeButton from '../ui/TerminalOrangeButton';
import {
  useGetSaleBundleQuery,
  useUpdateLineQuantityMutation,
  useConfirmLineStockMutation,
  useCompletePickingMutation,
  useReopenPickingMutation,
  useDeleteOrphanLineMutation,
} from '../../store/api/pickingApi';
import { useAuth } from '../../hooks/useAuth';
import { useBarcodeScannerWaiting } from '../../hooks/useBarcodeScannerWaiting';
import routes from '../../routes/routes';
import { formatMoney } from '../../helpers/dataFormatter';
import ProductImage from '../ui/ProductImage';
import BarcodeCameraDialog from './BarcodeCameraDialog';
import PickingLineCard, { getRowPickingStatus } from './PickingLineCard';
import PickingStatusDot from './PickingStatusDot';
import ProductPickDialog from './ProductPickDialog';
import PickingScanErrorDialog from './PickingScanErrorDialog';
import { fetcher } from '../../helpers/fetcher';
import { apiLinks } from '../../config/configApiLinks';
import {
  TERMINAL_ALPHANUM_KEYBOARD_LAYOUT,
  TERMINAL_ALPHANUM_KEYBOARD_DISPLAY,
} from '../../config/terminalKeyboardLayouts';
import {
  rowProduct,
  productMatchesCodeExact,
  findRowsForScanCode,
  rowIsOpenForPicking,
  rowIsFullyCollected,
  sortPickCandidateRows,
  rowToScanErrorProduct,
  catalogProductToScanError,
  shouldShowManualPickList,
  findPartialDocumentRows,
} from '../../helpers/pickingProductMatch';
import {
  buildNovaPoshtaExpressInvoicePdfUrl,
  buildNovaPoshtaMarkingsPdfUrl,
  openPdfUrlInBrowser,
} from '../../helpers/novaPoshtaPrint';
import { terminalColors, terminalBarIconButtonSx } from '../../theme/terminalStyles';
import { config } from '../../config/config';

const DETAIL_STATUS_DOT_SIZE = 40;


const DetailStatusDot = ({ row }) => (
  <PickingStatusDot status={getRowPickingStatus(row)} size={DETAIL_STATUS_DOT_SIZE} />
);

const formatArticle = (product) => {
  const a = product?.article;
  if (a == null || String(a).trim() === '') return '—';
  return String(a).trim();
};

const stableRowKey = (row) =>
  row?.saleProduct?.id != null ? `sp-${row.saleProduct.id}` : `pl-${row?.pickLine?.id}`;

const PickingWorkspacePage = () => {
  const { saleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { authState } = useAuth();
  const user = authState.user;

  const useSortOrderOnPicking = Boolean(user?.terminalWorkplace?.useSortOrderOnPicking);
  const printTtn = Boolean(user?.terminalWorkplace?.printTtn);
  const postServiceToken = String(user?.terminalWorkplace?.dsAccount?.token ?? '').trim();

  const sid = Number(saleId);
  const { data: bundle, isLoading, error } = useGetSaleBundleQuery(sid, { skip: !sid });
  const [updateQty, { isLoading: isSavingLine }] = useUpdateLineQuantityMutation();
  const [confirmStock, { isLoading: isConfirmingStock }] = useConfirmLineStockMutation();
  const [completePicking, { isLoading: isCompletingPicking }] = useCompletePickingMutation();
  const [reopenPicking, { isLoading: reopening }] = useReopenPickingMutation();
  const [deleteOrphanLine, { isLoading: deletingOrphan }] = useDeleteOrphanLineMutation();

  const [workspaceTab, setWorkspaceTab] = useState('items');
  const [sessionStarted, setSessionStarted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [detailRowKey, setDetailRowKey] = useState(null);
  const [toast, setToast] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [spotlightRowKey, setSpotlightRowKey] = useState(null);
  const [pickingSessionEnded, setPickingSessionEnded] = useState(false);
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const [reopenConfirmOpen, setReopenConfirmOpen] = useState(false);
  const [deleteOrphanRow, setDeleteOrphanRow] = useState(null);
  const [ttnPrintModalOpen, setTtnPrintModalOpen] = useState(false);
  const [productPickOpen, setProductPickOpen] = useState(false);
  const [productPickRows, setProductPickRows] = useState([]);
  const [scanErrorModal, setScanErrorModal] = useState(null);

  const detailCloseTimerRef = useRef(null);
  const manualKeyboardRef = useRef(null);
  const prevIsDoneRef = useRef(undefined);
  const spotlightTimerRef = useRef(null);

  const spickingIdFromNav = location.state?.spickingId;

  const activeSpicking = useMemo(() => {
    const list = bundle?.spickings || [];
    if (spickingIdFromNav) {
      const found = list.find((s) => s.id === spickingIdFromNav);
      if (found) return found;
    }
    const sorted = [...list].sort((a, b) => a.id - b.id);
    return sorted[0];
  }, [bundle, spickingIdFromNav]);

  const mergedRows = useMemo(() => {
    if (!bundle || !activeSpicking) return [];
    const saleProducts = [...(bundle.products || [])].sort(
      (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.id - b.id
    );
    const pickLines = activeSpicking.products || [];
    const pickedByPid = new Map(pickLines.map((p) => [p.productId, p]));

    const fromSale = saleProducts.map((sp, idx) => {
      const pl = pickedByPid.get(sp.productId);
      pickedByPid.delete(sp.productId);
      return { saleProduct: sp, pickLine: pl, displayOrder: idx + 1, isOrphan: false };
    });

    const orphanLines = [...pickedByPid.values()];
    const orphans = orphanLines.map((pl) => ({
      saleProduct: null,
      pickLine: pl,
      displayOrder: null,
      isOrphan: true,
      productFallback: pl.Product || pl.product,
    }));

    return [...fromSale, ...orphans];
  }, [bundle, activeSpicking]);

  const detailRow = useMemo(() => {
    if (!detailRowKey) return null;
    return mergedRows.find((r) => stableRowKey(r) === detailRowKey) || null;
  }, [mergedRows, detailRowKey]);

  const adjustPlusMinusEnabled =
    sessionStarted && !isPaused && !pickingSessionEnded && !activeSpicking?.isDone;

  const scanEnabledOuter =
    adjustPlusMinusEnabled && !completionModalOpen;

  const nextOpenSaleProduct = useMemo(() => {
    for (const row of mergedRows) {
      if (row.isOrphan) continue;
      const doc = Number(row.saleProduct.quantity);
      const fact = Number(row.pickLine?.quantity ?? 0);
      if (fact < doc - 0.0001) return row;
    }
    return null;
  }, [mergedRows]);

  const applyQtyToServer = useCallback(
    async (row, nextQty) => {
      if (!row?.pickLine || !activeSpicking || row.isOrphan) return null;
      return updateQty({
        spickingId: activeSpicking.id,
        lineId: row.pickLine.id,
        quantity: nextQty,
        saleId: sid,
      }).unwrap();
    },
    [activeSpicking, sid, updateQty]
  );

  const pulseRowOutline = useCallback((row) => {
    setSpotlightRowKey(stableRowKey(row));
    if (spotlightTimerRef.current) window.clearTimeout(spotlightTimerRef.current);
    spotlightTimerRef.current = window.setTimeout(() => setSpotlightRowKey(null), 2700);
  }, []);

  const applyScanToRow = useCallback(
    async (row) => {
      if (!row?.pickLine || !activeSpicking || row.isOrphan || !adjustPlusMinusEnabled) return;
      pulseRowOutline(row);

      const doc = Number(row.saleProduct.quantity);
      const cur = Number(row.pickLine.quantity ?? 0);
      const mult = Number(rowProduct(row.saleProduct)?.multiplicity ?? 1) || 1;
      const next = Math.min(doc, cur + mult);
      try {
        const response = await applyQtyToServer(row, next);
        const newFact = Number(response?.spickingProduct?.quantity ?? next);
        setWorkspaceTab('items');

        if (newFact < doc - 0.0001) {
          if (detailCloseTimerRef.current) window.clearTimeout(detailCloseTimerRef.current);
          setDetailRowKey(stableRowKey(row));
        } else {
          if (detailCloseTimerRef.current) window.clearTimeout(detailCloseTimerRef.current);
          detailCloseTimerRef.current = window.setTimeout(() => {
            setDetailRowKey(null);
          }, 1000);
        }
      } catch (e) {
        setToast({
          severity: 'error',
          message: (e && (e.data?.message || e.message)) || 'Помилка збереження',
        });
      }
    },
    [activeSpicking, applyQtyToServer, pulseRowOutline, adjustPlusMinusEnabled]
  );

  const closeScanErrorModal = useCallback(() => setScanErrorModal(null), []);

  const showAlreadyCollectedError = useCallback((row) => {
    setScanErrorModal({
      variant: 'already_collected',
      product: row ? rowToScanErrorProduct(row) : null,
    });
  }, []);

  const tryScanRow = useCallback(
    (row) => {
      if (!row?.pickLine || row.isOrphan) return false;
      if (rowIsFullyCollected(row)) {
        showAlreadyCollectedError(row);
        return false;
      }
      applyScanToRow(row);
      return true;
    },
    [applyScanToRow, showAlreadyCollectedError]
  );

  const applyScanCandidates = useCallback(
    (candidates, fromManual, code) => {
      let rows = candidates;
      if (fromManual) {
        const partialRows = findPartialDocumentRows(mergedRows, code);
        if (partialRows.length > 1) {
          rows = partialRows;
        } else if (partialRows.length === 1) {
          rows = partialRows;
        }
      }

      if (fromManual && shouldShowManualPickList(code, rows)) {
        setProductPickRows(sortPickCandidateRows(rows));
        setProductPickOpen(true);
        return;
      }

      if (rows.length > 1) {
        setProductPickRows(sortPickCandidateRows(rows));
        setProductPickOpen(true);
        return;
      }

      const row = rows[0];
      if (rowIsFullyCollected(row)) {
        showAlreadyCollectedError(row);
        return;
      }
      tryScanRow(row);
    },
    [mergedRows, tryScanRow, showAlreadyCollectedError]
  );

  const resolveProductNotInDocument = useCallback(
    async (code, fromManual) => {
      const token = user?.token;
      if (!token) {
        setToast({
          severity: 'warning',
          message: fromManual
            ? 'Товар не знайдено в документі'
            : 'Рядок не знайдено або порушено порядок сканування',
        });
        return;
      }
      try {
        const catalogProduct = await fetcher(
          apiLinks.findByBarcode,
          'POST',
          { barcode: code },
          true,
          token
        );
        if (catalogProduct?.id) {
          const inDocument = mergedRows.some(
            (r) => !r.isOrphan && Number(r.saleProduct?.productId) === Number(catalogProduct.id)
          );
          if (inDocument) {
            const docCandidates = findRowsForScanCode(mergedRows, code, {
              allowPartialArticle: fromManual,
            });
            if (docCandidates.length) {
              applyScanCandidates(docCandidates, fromManual, code);
              return;
            }
            const rowByProduct = mergedRows.find(
              (r) =>
                !r.isOrphan &&
                Number(r.saleProduct?.productId) === Number(catalogProduct.id)
            );
            if (rowByProduct) {
              applyScanCandidates([rowByProduct], fromManual, code);
              return;
            }
          }
          setScanErrorModal({
            variant: 'not_in_document',
            product: catalogProductToScanError(catalogProduct),
          });
          return;
        }
      } catch {
        /* каталог недоступний — показуємо загальне повідомлення */
      }
      setToast({
        severity: 'warning',
        message: fromManual
          ? 'Товар не знайдено в документі'
          : 'Рядок не знайдено або порушено порядок сканування',
      });
    },
    [user?.token, mergedRows, applyScanCandidates]
  );

  const processScanMatches = useCallback(
    (candidates, fromManual, code) => {
      if (!candidates.length) {
        void resolveProductNotInDocument(code, fromManual);
        return;
      }
      applyScanCandidates(candidates, fromManual, code);
    },
    [resolveProductNotInDocument, applyScanCandidates]
  );

  const handleScanCode = useCallback(
    (rawCode, fromManual = false) => {
      if (!scanEnabledOuter) return;
      if (!bundle || !activeSpicking || activeSpicking.isDone || pickingSessionEnded) return;

      const code = String(rawCode || '').trim();
      if (!code) return;

      if (!fromManual && useSortOrderOnPicking && nextOpenSaleProduct) {
        if (
          productMatchesCodeExact(rowProduct(nextOpenSaleProduct.saleProduct), code) &&
          !nextOpenSaleProduct.isOrphan
        ) {
          if (rowIsFullyCollected(nextOpenSaleProduct)) {
            showAlreadyCollectedError(nextOpenSaleProduct);
            return;
          }
          applyScanToRow(nextOpenSaleProduct);
          return;
        }
        const orderCandidates = findRowsForScanCode(mergedRows, code, {
          allowPartialArticle: false,
        });
        if (!orderCandidates.length) {
          void resolveProductNotInDocument(code, false);
          return;
        }
        setToast({
          severity: 'warning',
          message: 'Рядок не знайдено або порушено порядок сканування',
        });
        return;
      }

      if (detailRow && !detailRow.isOrphan) {
        if (productMatchesCodeExact(rowProduct(detailRow.saleProduct), code)) {
          if (rowIsFullyCollected(detailRow)) {
            showAlreadyCollectedError(detailRow);
            return;
          }
          applyScanToRow(detailRow);
          return;
        }
      }

      const candidates = findRowsForScanCode(mergedRows, code, {
        allowPartialArticle: fromManual,
      });
      processScanMatches(candidates, fromManual, code);
    },
    [
      scanEnabledOuter,
      bundle,
      activeSpicking,
      pickingSessionEnded,
      detailRow,
      useSortOrderOnPicking,
      nextOpenSaleProduct,
      mergedRows,
      applyScanToRow,
      processScanMatches,
      showAlreadyCollectedError,
      resolveProductNotInDocument,
    ]
  );

  const handleProductPickSelect = (row) => {
    setProductPickOpen(false);
    setProductPickRows([]);
    tryScanRow(row);
  };

  useBarcodeScannerWaiting(
    config.scanCodeLength,
    scanEnabledOuter && Boolean(bundle && activeSpicking && !activeSpicking?.isDone),
    [scanEnabledOuter, bundle, activeSpicking, pickingSessionEnded, completionModalOpen],
    { mthd: handleScanCode }
  );

  useEffect(() => {
    const done = Boolean(activeSpicking?.isDone);
    const prev = prevIsDoneRef.current;
    if (prev === undefined) {
      prevIsDoneRef.current = done;
      return undefined;
    }
    prevIsDoneRef.current = done;
    const becameDone = done && prev === false && sessionStarted && !pickingSessionEnded;
    if (!becameDone) return undefined;

    const t = window.setTimeout(() => setCompletionModalOpen(true), 1000);
    return () => window.clearTimeout(t);
  }, [activeSpicking?.isDone, sessionStarted, pickingSessionEnded]);

  useEffect(
    () => () => {
      if (detailCloseTimerRef.current) window.clearTimeout(detailCloseTimerRef.current);
      if (spotlightTimerRef.current) window.clearTimeout(spotlightTimerRef.current);
    },
    []
  );

  useEffect(() => {
    setPickingSessionEnded(false);
    setCompletionModalOpen(false);
    prevIsDoneRef.current = undefined;
  }, [sid]);

  if (!user?.terminalWorkplace?.isOrderPicking) {
    return <Navigate to={routes.root} replace />;
  }

  if (isLoading) {
    return (
      <TerminalLayout title="Збірка" titleIcon={<Inventory2OutlinedIcon />} backTo={routes.picking}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </TerminalLayout>
    );
  }

  if (error || !bundle || !activeSpicking) {
    return (
      <TerminalLayout title="Збірка" titleIcon={<Inventory2OutlinedIcon />} backTo={routes.picking}>
        <Typography color="error" sx={{ mt: 2 }}>
          Документ або збірку не знайдено. Поверніться до списку.
        </Typography>
        <TerminalOrangeButton sx={{ mt: 2 }} onClick={() => navigate(routes.picking)}>
          До списку
        </TerminalOrangeButton>
      </TerminalLayout>
    );
  }

  const changeQtyBy = async (row, deltaMult) => {
    if (!adjustPlusMinusEnabled || !row?.pickLine || row.isOrphan || !activeSpicking) return;
    const doc = Number(row.saleProduct.quantity);
    const cur = Number(row.pickLine.quantity ?? 0);
    const mult = Number(rowProduct(row.saleProduct)?.multiplicity ?? 1) || 1;
    const step = mult * deltaMult;
    let next = cur + step;
    next = Math.max(0, Math.min(doc, next));
    try {
      await applyQtyToServer(row, next);
      if (next >= doc - 0.0001 && detailRowKey === stableRowKey(row)) {
        if (detailCloseTimerRef.current) window.clearTimeout(detailCloseTimerRef.current);
        detailCloseTimerRef.current = window.setTimeout(() => setDetailRowKey(null), 1000);
      }
    } catch (e) {
      setToast({
        severity: 'error',
        message: (e && (e.data?.message || e.message)) || 'Помилка',
      });
    }
  };

  const handleStockConfirmToggle = async () => {
    if (!adjustPlusMinusEnabled || !detailRow?.pickLine || detailRow.isOrphan || !activeSpicking) return;
    try {
      await confirmStock({
        spickingId: activeSpicking.id,
        lineId: detailRow.pickLine.id,
        confirmed: !detailRow.pickLine.isStockConfirmed,
        saleId: sid,
      }).unwrap();
    } catch (e) {
      setToast({
        severity: 'error',
        message: (e && (e.data?.message || e.message)) || 'Помилка',
      });
    }
  };

  const handleCompletePicking = async () => {
    if (!activeSpicking?.id || activeSpicking.isDone || !sessionStarted || isPaused) return;
    try {
      await completePicking({ spickingId: activeSpicking.id, saleId: sid }).unwrap();
    } catch (e) {
      setToast({
        severity: 'error',
        message: (e && (e.data?.message || e.message)) || 'Помилка',
      });
    }
  };

  const handleSave = () => {
    setToast({ severity: 'info', message: 'Дані збережено на сервері' });
  };

  const togglePlayPauseLock = () => {
    if (activeSpicking?.isDone) {
      setReopenConfirmOpen(true);
      return;
    }
    if (!sessionStarted) {
      setSessionStarted(true);
      setIsPaused(false);
      window.requestAnimationFrame(() => document.activeElement?.blur?.());
    } else {
      setIsPaused((p) => !p);
    }
  };

  const handleDeleteOrphanRequest = (row) => {
    setDeleteOrphanRow(row);
  };

  const handleDeleteOrphanConfirm = async () => {
    if (!deleteOrphanRow?.pickLine?.id || !activeSpicking?.id) return;
    try {
      await deleteOrphanLine({
        spickingId: activeSpicking.id,
        lineId: deleteOrphanRow.pickLine.id,
        saleId: sid,
      }).unwrap();
      if (stableRowKey(deleteOrphanRow) === detailRowKey) {
        setDetailRowKey(null);
      }
      setDeleteOrphanRow(null);
      setToast({ severity: 'success', message: 'Товар видалено' });
    } catch (e) {
      setToast({
        severity: 'error',
        message: (e && (e.data?.message || e.message)) || 'Помилка видалення',
      });
    }
  };

  const handleReopenConfirm = async () => {
    if (!activeSpicking?.id) return;
    try {
      await reopenPicking({
        spickingId: activeSpicking.id,
        saleId: sid,
      }).unwrap();
      setReopenConfirmOpen(false);
      setPickingSessionEnded(false);
      setCompletionModalOpen(false);
      prevIsDoneRef.current = false;
      setSessionStarted(false);
      setIsPaused(false);
      setToast({ severity: 'success', message: 'Режим збірки доступний' });
    } catch (e) {
      setToast({
        severity: 'error',
        message: (e && (e.data?.message || e.message)) || 'Помилка',
      });
    }
  };

  const bottomScanEnabled = scanEnabledOuter;
  const completePickingDisabled =
    Boolean(activeSpicking?.isDone) ||
    !sessionStarted ||
    isPaused ||
    isCompletingPicking;

  const headerEndActions = activeSpicking?.isDone ? (
    <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 28 }} aria-hidden />
  ) : (
    <RadioButtonUncheckedIcon
      sx={{ color: terminalColors.accentOrange, fontSize: 28 }}
      aria-hidden
    />
  );

  const bottomBar = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: 0.75,
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
        <IconButton
          aria-label={activeSpicking?.isDone ? 'документ зібрано' : sessionStarted ? 'пауза' : 'старт'}
          onClick={togglePlayPauseLock}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
          }}
          sx={terminalBarIconButtonSx}
        >
          {activeSpicking?.isDone ? (
            <LockOutlinedIcon />
          ) : !sessionStarted || isPaused ? (
            <PlayArrowIcon />
          ) : (
            <PauseIcon />
          )}
        </IconButton>
        <IconButton
          aria-label="штрихкод"
          disabled={!bottomScanEnabled}
          onClick={() => setManualOpen(true)}
          sx={terminalBarIconButtonSx}
        >
          <KeyboardIcon />
        </IconButton>
        <IconButton
          disabled={!bottomScanEnabled}
          aria-label="камера"
          onClick={() => setCameraOpen(true)}
          sx={terminalBarIconButtonSx}
        >
          <PhotoCameraIcon />
        </IconButton>
      </Box>
      <IconButton
        aria-label="підтвердити збірку"
        disabled={completePickingDisabled}
        onClick={handleCompletePicking}
        sx={{
          ...terminalBarIconButtonSx,
          ml: 'auto',
          flexShrink: 0,
          ...(activeSpicking?.isDone
            ? { color: '#2e7d32' }
            : { color: terminalColors.accentOrange }),
        }}
      >
        {isCompletingPicking ? (
          <CircularProgress size={22} color="inherit" />
        ) : (
          <CheckCircleIcon />
        )}
      </IconButton>
      <IconButton
        aria-label="зберегти"
        onClick={handleSave}
        sx={{ ...terminalBarIconButtonSx, flexShrink: 0 }}
      >
        <SaveOutlinedIcon />
      </IconButton>
    </Box>
  );

  const title = `Збірка №${bundle.number || bundle.id}`;

  const docDate =
    bundle.date != null ? new Date(bundle.date).toLocaleDateString('uk-UA', { dateStyle: 'short' }) : '—';

  const ttnText = bundle.ttnNumber?.trim?.() ? String(bundle.ttnNumber).trim() : '';
  const hasTtn = Boolean(ttnText);

  const detailDocQty = detailRow?.saleProduct ? Number(detailRow.saleProduct.quantity) : 0;
  const detailStockQty = Number(detailRow?.pickLine?.quantityStockCurrent ?? 0);
  const detailFactQty = Number(detailRow?.pickLine?.quantity ?? 0);
  const detailStockConfirmDisabled =
    !adjustPlusMinusEnabled || isConfirmingStock || !detailRow?.pickLine || detailRow.isOrphan;
  const detailMinusDisabled =
    !adjustPlusMinusEnabled || isSavingLine || detailFactQty <= 0.0001;
  const detailPlusDisabled =
    !adjustPlusMinusEnabled ||
    isSavingLine ||
    (detailDocQty > 0 && detailFactQty >= detailDocQty - 0.0001);

  const openTtnPrintModal = () => {
    if (!hasTtn) return;
    setTtnPrintModalOpen(true);
  };

  const handleNpPdfChoice = (buildUrl) => {
    const url = buildUrl(ttnText, postServiceToken);
    if (!url) {
      setToast({
        severity: 'error',
        message: 'Не вдалося сформувати посилання. Перевірте номер ТТН і токен у робочому місці.',
      });
      return;
    }
    const ok = openPdfUrlInBrowser(url);
    setTtnPrintModalOpen(false);
    if (!ok) {
      setToast({
        severity: 'warning',
        message:
          'Браузер заблокував відкриття. Дозвіть спливаючі вікна для сайту або відкрийте PDF із історії завантажень.',
      });
    }
  };

  return (
    <TerminalLayout
      title={title}
      titleIcon={<Inventory2OutlinedIcon />}
      backTo={routes.picking}
      endActions={headerEndActions}
      bottomBar={bottomBar}
    >
      {!sessionStarted && !activeSpicking.isDone ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {/* Натисніть «Стрілка відтворення» на нижній панелі, щоб перейти в режим збірки. */}
          {bundle.comment?.trim() ? bundle.comment : 'Натисніть кнопку "Старт" на нижній панелі, щоб почати збірку.'}
        </Alert>
      ) : null}
      {bundle.isCollected ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Документ позначено як зібрано. Переглядайте без змін або відкрийте режим збірки через іконку замка.
        </Alert>
      ) : null}

      <Paper sx={{ mb: 1.5, p: 0.5 }} elevation={0}>
        <ToggleButtonGroup
          exclusive
          value={workspaceTab}
          onChange={(e, v) => {
            if (v) setWorkspaceTab(v);
          }}
          fullWidth
          size="small"
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600 } }}
        >
          <ToggleButton value="items">Товари{bundle.packageInfo?.trim?.() ? ` - упак.: (${bundle.packageInfo})` : ''}</ToggleButton>
          <ToggleButton value="doc">Документ</ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {workspaceTab === 'doc' ? (
        <Paper
          sx={{ p: 2, bgcolor: terminalColors.cardWhite, borderRadius: 2 }}
          elevation={0}
        >
          <Typography variant="body2">
            <strong>Номер:</strong> {bundle.number || '—'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Дата:</strong> {docDate}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Контрагент:</strong> {bundle.clientName || '—'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Сума:</strong> {formatMoney(bundle.sum ?? 0)} ₴
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Служба доставки:</strong> {bundle.deliveryServiceName || '—'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Адреса доставки:</strong>{' '}
            {bundle.deliveryInfo?.trim?.() ? bundle.deliveryInfo : '—'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Коментар:</strong>{' '}
            {bundle.comment?.trim?.() ? bundle.comment : '—'}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mt: 1,
              width: '100%',
              minWidth: 0,
            }}
          >
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0, pr: 1 }}>
              <strong>ТТН:</strong>{' '}
              <Box component="span" sx={{ wordBreak: 'break-all' }}>
                {hasTtn ? ttnText : '—'}
              </Box>
            </Typography>
            {printTtn ? (
              <IconButton
                aria-label="друк ТТН"
                onClick={openTtnPrintModal}
                disabled={!hasTtn}
                sx={{ ...terminalBarIconButtonSx, flexShrink: 0 }}
              >
                <PrintOutlinedIcon />
              </IconButton>
            ) : null}
          </Box>
        </Paper>
      ) : (
        <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
          {mergedRows.map((row) => (
            <PickingLineCard
              key={stableRowKey(row)}
              row={row}
              product={row.isOrphan ? row.productFallback || row.pickLine?.Product : rowProduct(row.saleProduct)}
              isSpotlighted={stableRowKey(row) === spotlightRowKey}
              deleteEnabled={adjustPlusMinusEnabled}
              onDelete={handleDeleteOrphanRequest}
              onOpen={() => {
                setWorkspaceTab('items');
                setDetailRowKey(stableRowKey(row));
              }}
            />
          ))}
        </Box>
      )}

      <Modal open={manualOpen} onClose={() => setManualOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 400,
            bgcolor: 'background.paper',
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Введіть штрихкод / артикул
          </Typography>
          <TextField
            fullWidth
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            margin="normal"
          />
          <Keyboard
            keyboardRef={(r) => {
              manualKeyboardRef.current = r;
            }}
            onChange={setManualCode}
            layout={TERMINAL_ALPHANUM_KEYBOARD_LAYOUT}
            display={TERMINAL_ALPHANUM_KEYBOARD_DISPLAY}
            onKeyPress={(button) => {
              if (button === '{bksp}') {
                setManualCode((prev) => {
                  const next = prev.slice(0, -1);
                  manualKeyboardRef.current?.setInput(next);
                  return next;
                });
              }
            }}
          />
          <TerminalOrangeButton
            fullWidth
            sx={{ mt: 1 }}
            startIcon={<PhotoCameraIcon />}
            disabled={!bottomScanEnabled}
            onClick={() => {
              setManualOpen(false);
              setCameraOpen(true);
            }}
          >
            Сканувати з камери
          </TerminalOrangeButton>
          <TerminalOrangeButton
            fullWidth
            sx={{ mt: 2 }}
            onClick={() => {
              handleScanCode(manualCode, true);
              setManualCode('');
              manualKeyboardRef.current?.clearInput();
              setManualOpen(false);
            }}
          >
            OK
          </TerminalOrangeButton>
        </Box>
      </Modal>

      <BarcodeCameraDialog
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCode={(code) => handleScanCode(code, false)}
      />

      <ProductPickDialog
        open={productPickOpen}
        rows={productPickRows}
        onClose={() => {
          setProductPickOpen(false);
          setProductPickRows([]);
        }}
        onSelect={handleProductPickSelect}
      />

      <Dialog
        open={Boolean(detailRow)}
        onClose={() => setDetailRowKey(null)}
        fullScreen
        PaperProps={{ sx: { bgcolor: terminalColors.gradientTop } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
          }}
        >
          <Typography variant="h6" component="span" sx={{ flex: 1 }}>
            {detailRow && !detailRow.isOrphan
              ? `#${detailRow.displayOrder} з ${mergedRows.filter((r) => !r.isOrphan).length}`
              : 'Рядок'}
          </Typography>
          <IconButton
            aria-label="Закрити"
            onClick={() => setDetailRowKey(null)}
            sx={{
              bgcolor: terminalColors.accentOrange,
              color: terminalColors.textPrimary,
              '&:hover': { bgcolor: terminalColors.accentOrangeHover },
            }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {detailRow ? (
            <Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                Артикул:{' '}
                <strong>
                  {formatArticle(
                    detailRow.isOrphan
                      ? detailRow.productFallback || detailRow.pickLine?.Product
                      : rowProduct(detailRow.saleProduct)
                  )}
                </strong>
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ wordBreak: 'break-word' }}>
                {(detailRow.isOrphan
                  ? `${detailRow.productFallback?.name || ''} ${detailRow.productFallback?.modName || ''}`
                  : `${rowProduct(detailRow.saleProduct)?.name || ''} ${
                      rowProduct(detailRow.saleProduct)?.modName || ''
                    }`
                ).trim() || '—'}
              </Typography>

              {detailRow.isOrphan ? (
                <>
                  <Typography color="error" sx={{ mb: 1 }}>
                    Товар поза поточним документом реалізації.
                  </Typography>
                  {adjustPlusMinusEnabled ? (
                    <TerminalOrangeButton
                      fullWidth
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => handleDeleteOrphanRequest(detailRow)}
                      sx={{
                        bgcolor: '#ef5350',
                        color: '#fff',
                        '&:hover': { bgcolor: '#e53935' },
                      }}
                    >
                      Видалити товар
                    </TerminalOrangeButton>
                  ) : null}
                </>
              ) : (
                <>
                  <ProductImage
                    image={rowProduct(detailRow.saleProduct)?.imageUrl ?? rowProduct(detailRow.saleProduct)?.image}
                    sx={{ width: '100%', maxHeight: 280, my: 2 }}
                    placeholderSx={{ width: '100%', maxHeight: 280, my: 2 }}
                  />
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    Залишок: <strong>{detailStockQty}</strong>
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    Док: <strong>{detailRow.saleProduct.quantity}</strong>
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      mt: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography variant="h6">
                      Факт: <strong>{detailRow.pickLine?.quantity ?? 0}</strong>
                    </Typography>
                    <DetailStatusDot row={detailRow} />
                  </Box>
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    Кратність: {rowProduct(detailRow.saleProduct)?.multiplicity ?? 1}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Зібрав:{' '}
                    <strong>
                      {detailRow.pickLine?.user?.name ||
                        detailRow.pickLine?.user?.login ||
                        detailRow.pickLine?.pickedByUser?.name ||
                        detailRow.pickLine?.pickedByUser?.login ||
                        '—'}
                    </strong>
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(detailRow.pickLine?.isStockConfirmed)}
                        onChange={handleStockConfirmToggle}
                        disabled={detailStockConfirmDisabled}
                        sx={{
                          color: terminalColors.accentOrange,
                          '&.Mui-checked': { color: terminalColors.accentOrange },
                        }}
                      />
                    }
                    label="Залишок вірний"
                    sx={{ mt: 1.5, display: 'flex', alignItems: 'center' }}
                  />

                  <Box sx={{ display: 'flex', gap: 3, mt: 3, justifyContent: 'center' }}>
                    <IconButton
                      size="large"
                      onClick={() => changeQtyBy(detailRow, -1)}
                      disabled={detailMinusDisabled}
                      sx={{
                        width: 72,
                        height: 72,
                        bgcolor: terminalColors.accentOrange,
                        '&:hover': { bgcolor: terminalColors.accentOrangeHover },
                      }}
                    >
                      <RemoveIcon fontSize="large" />
                    </IconButton>
                    <IconButton
                      size="large"
                      onClick={() => changeQtyBy(detailRow, 1)}
                      disabled={detailPlusDisabled}
                      sx={{
                        width: 72,
                        height: 72,
                        bgcolor: terminalColors.accentOrange,
                        '&:hover': { bgcolor: terminalColors.accentOrangeHover },
                      }}
                    >
                      <AddIcon fontSize="large" />
                    </IconButton>
                  </Box>
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 3, pt: 0 }}>
          <TerminalOrangeButton fullWidth size="large" onClick={() => setDetailRowKey(null)}>
            Закрити
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <Dialog open={ttnPrintModalOpen} onClose={() => setTtnPrintModalOpen(false)}>
        <DialogTitle sx={{ pb: 0.5 }}>Друк Нової Пошти</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            ТТН:{' '}
            <Box component="span" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
              {ttnText}
            </Box>
          </Typography>
          {!postServiceToken ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Не налаштовано підключення до Нової Пошти
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Оберіть тип документа — PDF відкриється в новій вкладці або завантажиться (залежно від
              браузера на телефоні).
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1.25, px: 2, pb: 2.5, pt: 0 }}>
          <TerminalOrangeButton
            fullWidth
            disabled={!postServiceToken}
            onClick={() => handleNpPdfChoice(buildNovaPoshtaExpressInvoicePdfUrl)}
          >
            Експрес-накладна
          </TerminalOrangeButton>
          <TerminalOrangeButton
            fullWidth
            disabled={!postServiceToken}
            onClick={() => handleNpPdfChoice(buildNovaPoshtaMarkingsPdfUrl)}
          >
            Маркування
          </TerminalOrangeButton>
          <TerminalOrangeButton
            fullWidth
            onClick={() => setTtnPrintModalOpen(false)}
            sx={{
              bgcolor: 'transparent',
              color: 'text.primary',
              boxShadow: 'none',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', boxShadow: 'none' },
            }}
          >
            Скасувати
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <PickingScanErrorDialog
        open={Boolean(scanErrorModal)}
        variant={scanErrorModal?.variant}
        product={scanErrorModal?.product}
        onClose={closeScanErrorModal}
      />

      <Dialog open={completionModalOpen} onClose={() => {}} disableEscapeKeyDown>
        <DialogContent sx={{ textAlign: 'center', py: 3, px: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 72, color: '#2e7d32', mb: 1 }} />
          <Typography variant="h6" sx={{ px: 1 }}>
            Документ зібрано!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Збірку завершено. Сканування та зміна кількостей недоступні, доки ви не розблокуєте збірку.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <TerminalOrangeButton
            onClick={() => {
              setCompletionModalOpen(false);
              setPickingSessionEnded(true);
              setSessionStarted(false);
              setIsPaused(false);
              setDetailRowKey(null);
            }}
          >
            OK
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteOrphanRow)} onClose={() => setDeleteOrphanRow(null)}>
        <DialogTitle>Видалити товар?</DialogTitle>
        <DialogContent>
          <Typography>
            Товар відсутній у поточному документі. Видалити рядок зі збірки?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <TerminalOrangeButton
            onClick={() => setDeleteOrphanRow(null)}
            sx={{ bgcolor: 'transparent', color: 'text.primary', boxShadow: 'none' }}
          >
            Ні
          </TerminalOrangeButton>
          <TerminalOrangeButton disabled={deletingOrphan} onClick={handleDeleteOrphanConfirm}>
            Так
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <Dialog open={reopenConfirmOpen} onClose={() => setReopenConfirmOpen(false)}>
        <DialogTitle>Увага!</DialogTitle>
        <DialogContent>
          <Typography>Документ уже зібрано! Дозволити перейти в режим збірки?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <TerminalOrangeButton
            onClick={() => setReopenConfirmOpen(false)}
            sx={{ bgcolor: 'transparent', color: 'text.primary', boxShadow: 'none' }}
          >
            Ні
          </TerminalOrangeButton>
          <TerminalOrangeButton disabled={reopening} onClick={handleReopenConfirm}>
            Так
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : null}
      </Snackbar>
    </TerminalLayout>
  );
};

export default PickingWorkspacePage;
